// src/components/ao/AOModal.jsx
import React, { useState, useCallback } from 'react';
import { aoAPI, apiLong } from '../../services/api';
import { extractError } from '../../context/AuthContext';
import { X, Upload, FileText, Loader, CheckCircle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';

const EMPTY = {
  job_title: '', client: '', location: '', source_type: 'Manuel',
  contract_type: '', work_mode: '', experience_years: '',
  detailed_description: '', tech_skills: '', soft_skills: '',
};

export default function AOModal({ onClose, onSaved }) {
  const [form,      setForm]      = useState(EMPTY);
  const [loading,   setLoading]   = useState(false);
  const [tab,       setTab]       = useState('form');
  const [file,      setFile]      = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extracted,  setExtracted]  = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Soumission formulaire ─────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        tech_skills: form.tech_skills ? form.tech_skills.split(',').map(s => s.trim()).filter(Boolean) : [],
        soft_skills: form.soft_skills ? form.soft_skills.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      await aoAPI.create(payload);
      toast.success('AO créé avec succès');
      onSaved();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  // ── Upload + extraction IA ────────────────────────────────────
  const onDrop = useCallback((accepted) => {
    if (accepted[0]) setFile(accepted[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
      'text/plain': [],
    },
    maxFiles: 1,
  });

  const handleExtract = async () => {
    if (!file) return;
    setExtracting(true);
    try {
      // 1. Envoyer le fichier à /cv/convert pour extraire le texte
      const formData = new FormData();
      formData.append('file', file);

      // On utilise un endpoint dédié d'extraction de texte brut
      // Fallback : lire le texte via FileReader si c'est un txt
      let rawText = '';
      if (file.type === 'text/plain') {
        rawText = await file.text();
      } else {
        // Utiliser l'endpoint backend pour extraire le texte du PDF/DOCX
        const extractRes = await apiLong.post('/ao-management/extract-text', formData);
        rawText = extractRes.data.text || '';
      }

      if (!rawText.trim()) {
        toast.error('Impossible d\'extraire le texte du fichier');
        return;
      }

      // 2. Envoyer le texte à l'endpoint de traitement AO IA
      toast('Analyse par l\'IA en cours…', { icon: '⏳' });
      const processRes = await apiLong.post('/ao-management/process-text', { raw_text: rawText });
      const worked = processRes.data;

      // 3. Pré-remplir le formulaire avec les données extraites
      setForm({
        job_title:            worked.job_title            || '',
        client:               worked.client               || '',
        location:             worked.location             || '',
        source_type:          'Manuel',
        contract_type:        worked.contract_type        || '',
        work_mode:            worked.work_mode            || '',
        experience_years:     worked.experience_years     || '',
        detailed_description: worked.detailed_description || worked.executive_summary || '',
        tech_skills:          (worked.tech_skills   || []).join(', '),
        soft_skills:          (worked.soft_skills   || []).join(', '),
      });

      setExtracted(true);
      setTab('form'); // Passer au formulaire pour vérifier/corriger
      toast.success('Fiche analysée — vérifiez et complétez si besoin');
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <h3>Nouvel appel d'offres</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18}/></button>
        </div>

        <div style={{ padding: '0 28px' }}>
          <div className="tabs" style={{ width: 'fit-content', marginBottom: 0 }}>
            <button className={`tab ${tab === 'form' ? 'active' : ''}`} onClick={() => setTab('form')}>
              <FileText size={14}/> Formulaire {extracted && <CheckCircle size={12} color="var(--success-600)" style={{ marginLeft: 4 }}/>}
            </button>
            <button className={`tab ${tab === 'file' ? 'active' : ''}`} onClick={() => setTab('file')}>
              <Upload size={14}/> Fiche de poste (IA)
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">

            {/* ── Onglet Upload IA ── */}
            {tab === 'file' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Zone dropzone */}
                <div {...getRootProps()} className={`upload-zone ${isDragActive ? 'drag-over' : ''}`}
                  style={{ minHeight: 160, cursor: 'pointer' }}>
                  <input {...getInputProps()} />
                  <Upload size={28} color={isDragActive ? 'var(--blue-600)' : 'var(--text-muted)'}/>
                  <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.9375rem' }}>
                    {isDragActive ? 'Déposez ici…' : 'Glissez la fiche de poste ou cliquez pour parcourir'}
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>PDF, DOCX, TXT</div>
                  <button type="button" className="btn btn-secondary btn-sm">
                    Parcourir
                  </button>
                </div>

                {/* Fichier sélectionné */}
                {file && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                    background: 'var(--blue-50)', border: '1px solid var(--blue-100)',
                    borderRadius: 'var(--radius-md)' }}>
                    <FileText size={18} color="var(--blue-600)"/>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{file.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {(file.size / 1024).toFixed(0)} Ko
                      </div>
                    </div>
                    <button type="button" className="btn btn-ghost btn-sm"
                      onClick={() => { setFile(null); setExtracted(false); }}>
                      <X size={14}/>
                    </button>
                  </div>
                )}

                {/* Info */}
                <div style={{ padding: '10px 14px', background: 'var(--blue-50)',
                  border: '1px solid var(--blue-100)', borderRadius: 'var(--radius-md)',
                  fontSize: '0.8125rem', color: 'var(--blue-700)' }}>
                  💡 L'IA va analyser la fiche de poste et pré-remplir le formulaire automatiquement.
                  Vous pourrez vérifier et corriger avant de créer l'AO.
                </div>

                {/* Bouton extraction */}
                <button type="button" className="btn btn-orange"
                  onClick={handleExtract}
                  disabled={!file || extracting}
                  style={{ width: '100%', justifyContent: 'center' }}>
                  {extracting
                    ? <><Loader size={15} className="spin"/> Analyse IA en cours…</>
                    : <><Upload size={15}/> Analyser avec l'IA</>}
                </button>
              </div>
            )}

            {/* ── Onglet Formulaire ── */}
            {tab === 'form' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {extracted && (
                  <div style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 14px', background: 'var(--success-50)',
                    border: '1px solid var(--success-200)', borderRadius: 'var(--radius-md)',
                    fontSize: '0.8125rem', color: 'var(--success-700)' }}>
                    <CheckCircle size={14}/>
                    Formulaire pré-rempli par l'IA — vérifiez et complétez si nécessaire.
                    <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
                      onClick={() => setTab('file')}>
                      <RefreshCw size={12}/> Recharger une fiche
                    </button>
                  </div>
                )}
                <div className="input-group" style={{ gridColumn: '1/-1' }}>
                  <label className="input-label">Intitulé du poste <span>*</span></label>
                  <input className="input" required value={form.job_title}
                    onChange={e => set('job_title', e.target.value)} placeholder="Ex: Chef de Projet SAP"/>
                </div>
                <div className="input-group">
                  <label className="input-label">Client <span>*</span></label>
                  <input className="input" required value={form.client}
                    onChange={e => set('client', e.target.value)} placeholder="Nom du client"/>
                </div>
                <div className="input-group">
                  <label className="input-label">Localisation</label>
                  <input className="input" value={form.location}
                    onChange={e => set('location', e.target.value)} placeholder="Paris, Lyon…"/>
                </div>
                <div className="input-group">
                  <label className="input-label">Source</label>
                  <select className="input select" value={form.source_type}
                    onChange={e => set('source_type', e.target.value)}>
                    {['Manuel','APEC','LinkedIn','Indeed','Turnover','Autre'].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="input-group">
                  <label className="input-label">Type de contrat</label>
                  <select className="input select" value={form.contract_type}
                    onChange={e => set('contract_type', e.target.value)}>
                    <option value="">— Sélectionner —</option>
                    {['CDI','CDD','Freelance','Alternance','Stage'].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="input-group">
                  <label className="input-label">Mode de travail</label>
                  <select className="input select" value={form.work_mode}
                    onChange={e => set('work_mode', e.target.value)}>
                    <option value="">— Sélectionner —</option>
                    {['Présentiel','Hybride','Télétravail total'].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="input-group">
                  <label className="input-label">Années d'expérience requises</label>
                  <input className="input" value={form.experience_years}
                    onChange={e => set('experience_years', e.target.value)} placeholder="Ex: 5-8 ans"/>
                </div>
                <div className="input-group" style={{ gridColumn: '1/-1' }}>
                  <label className="input-label">Compétences techniques</label>
                  <input className="input" value={form.tech_skills}
                    onChange={e => set('tech_skills', e.target.value)}
                    placeholder="Java, Spring, AWS, Docker… (séparés par virgule)"/>
                </div>
                <div className="input-group" style={{ gridColumn: '1/-1' }}>
                  <label className="input-label">Compétences comportementales</label>
                  <input className="input" value={form.soft_skills}
                    onChange={e => set('soft_skills', e.target.value)}
                    placeholder="Leadership, Communication… (séparés par virgule)"/>
                </div>
                <div className="input-group" style={{ gridColumn: '1/-1' }}>
                  <label className="input-label">Description du besoin</label>
                  <textarea className="textarea" rows={5} value={form.detailed_description}
                    onChange={e => set('detailed_description', e.target.value)}
                    placeholder="Contexte, missions, profil recherché…"/>
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            {tab === 'file' ? (
              <button type="button" className="btn btn-secondary"
                onClick={() => setTab('form')} disabled={!extracted}>
                Voir le formulaire →
              </button>
            ) : (
              <button type="submit" className="btn btn-orange" disabled={loading}>
                {loading
                  ? <><div className="spinner" style={{ width: 16, height: 16 }}/> Enregistrement…</>
                  : "Créer l'AO"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}